import React, { useState } from 'react';
import { Modal, Stepper, Step, StepButton, Button } from '@common-ui/ui';
import Report from '../ImageEditor/Report';
import { ThemeProvider, styled } from '@mui/material/styles';
import DarkTheme from '../../../../../styles/DarkTheme';
import { ModalContext } from '../..';
import { useContext } from 'react';
import SelectImages from './SelectImages';

/**
 * This is a styled component that will overwrite the default styling of the MUI Step component.
 *
 * @type {import('@mui/material/Step').StepProps}
 */
const BlueStep = styled(Step)(() => ({
  '& .MuiStepIcon-root': {
    fill: '#555',
  },
  '& .MuiStepIcon-root.Mui-active': {
    fill: '#2B7CD3',
  },
  '& .MuiStepIcon-text': {
    fill: 'rgb(255, 255, 255)',
  },
}));

/**
 * This component is the stepper that will be used to navigate through the report creation process
 * for the Image Editor modal
 *
 * @returns {JSX.Element}
 */
export default function LinearStepper() {
  const { closeModal } = useContext(ModalContext);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedImageIds, setSelectedImageIds] = useState([]);

  /**
   * This function will show the step that is currently active
   * It will show the SelectImages component if the active step is 0
   * It will show the Report component if the active step is 1
   *
   * @returns {JSX.Element} - The step that is currently active
   */
  const showSteps = () => {
    if (activeStep === 0) {
      return (
        <SelectImages
          selectedImageIds={selectedImageIds}
          setSelectedImageIds={setSelectedImageIds}
        />
      );
    }
    if (activeStep === 1) {
      return <Report selectedImageIds={selectedImageIds} />;
    }
  };

  return (
    <ThemeProvider theme={DarkTheme}>
      <Modal open={true} onClose={closeModal}>
        <div style={styles.container}>
          <div style={styles.content}>
            <Stepper nonLinear sx={styles.stepper} activeStep={activeStep}>
              {['Select Images', 'Build A Report'].map((label, index) => (
                <BlueStep key={label}>
                  <StepButton onClick={() => setActiveStep(index)}>{label}</StepButton>
                </BlueStep>
              ))}
            </Stepper>
          </div>
          <div>
            <React.Fragment>
              <div style={styles.content}>{showSteps(activeStep)}</div>
              <div style={styles.buttons}>
                <Button
                  color="inherit"
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep((prevActiveStep) => prevActiveStep - 1)}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  sx={{ visibility: activeStep === 1 ? 'hidden' : '' }}
                  color="secondary"
                  onClick={() => setActiveStep(activeStep + 1)}
                >
                  Next
                </Button>
              </div>
            </React.Fragment>
          </div>
        </div>
      </Modal>
    </ThemeProvider>
  );
}

const styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '960px',
    height: '700px',
    boxShadow: 24,
    padding: '36px',
    background: '#232323',
    border: '2px solid #000',
    color: 'white',
  },
  content: {
    display: 'flex',
    justifyContent: 'center',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  stepper: {
    width: '500px',
  },
};
